// Pixel Tracking Service
// Handles Facebook and TikTok Pixel initialization and event tracking

import { getUtmParams } from "../lib/utm";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

class PixelService {
  constructor() {
    this.initialized = false;
    this.pixels = [];
    this._pending = []; // track* calls made before init() finished (pixels load ~2s after mount)
    this.facebookInitialized = false;
    this.tiktokInitialized = false;
    this.kwaiInitialized = false;
    this.userData = {}; // { email, phone } for advanced matching
  }

  // Kwai Ads appends `click_id` to the landing URL; persisted so the Event API can attribute later events
  _getKwaiClickId() {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get('click_id') || new URLSearchParams(window.location.search).get('kwai_click_id');
      if (fromUrl) localStorage.setItem('ms_kwai_clickid', fromUrl.slice(0, 256));
      return localStorage.getItem('ms_kwai_clickid') || null;
    } catch (e) {
      return null;
    }
  }

  // ---- Advanced tracking helpers ----
  _genEventId() {
    return 'ev_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }

  // Drop an identical event (name + ids) fired twice within 2s (e.g. React double effects)
  _isDuplicate(eventName, key) {
    const k = `${eventName}:${key || ''}`;
    const now = Date.now();
    this._recent = this._recent || {};
    if (this._recent[k] && now - this._recent[k] < 2000) return true;
    this._recent[k] = now;
    return false;
  }

  _getCookie(name) {
    if (typeof document === 'undefined') return '';
    const m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
    return m ? decodeURIComponent(m.pop()) : '';
  }

  // Guarantee a `_fbp` cookie exists BEFORE the first event fires. fbevents.js loads async, so
  // early PageView/ViewContent used to reach CAPI without fbp (~32% coverage). Meta's official
  // format is `fb.<subdomainIndex>.<creationTime>.<randomNumber>`; the pixel reuses an existing cookie.
  _ensureFbp() {
    try {
      const existing = this._getCookie('_fbp');
      if (existing && existing.startsWith('fb.')) return existing;
      const rand = Math.floor(Math.random() * 9000000000) + 1000000000;
      const host = window.location.hostname;
      const labels = host.split('.');
      const keep = /\.(com|net|org|gov|edu)\.br$/i.test(host) ? 3 : 2;
      const domain = labels.length > keep && !/^\d+\.\d+\.\d+\.\d+$/.test(host) ? `.${labels.slice(-keep).join('.')}` : '';
      const fbp = `fb.${Math.max(labels.length - keep, 1)}.${Date.now()}.${rand}`;
      document.cookie = `_fbp=${fbp}; max-age=7776000; path=/; SameSite=Lax${domain ? `; domain=${domain}` : ''}${window.location.protocol === 'https:' ? '; Secure' : ''}`;
      return this._getCookie('_fbp') || fbp;
    } catch (e) {
      return '';
    }
  }

  // Meta attribution to persist on the order: _fbp, _fbc (or built from ?fbclid) and the checkout URL.
  getAttribution() {
    try {
      let fbc = this._getCookie('_fbc') || '';
      if (!fbc) {
        const fbclid = new URLSearchParams(window.location.search).get('fbclid') || sessionStorage.getItem('ms_fbclid') || '';
        if (fbclid) fbc = `fb.1.${Date.now()}.${fbclid}`;
      }
      const fbclidNow = new URLSearchParams(window.location.search).get('fbclid');
      if (fbclidNow) sessionStorage.setItem('ms_fbclid', fbclidNow);
      return {
        fbp: this._ensureFbp() || null,
        fbc: fbc || null,
        kwai_click_id: this._getKwaiClickId(),
        event_source_url: window.location.href.split('#')[0].slice(0, 512),
        referrer_url: this._getReferrer(),
        client_ip: this.clientIp || null,
      };
    } catch (e) {
      return { fbp: null, fbc: null, kwai_click_id: null, event_source_url: null, referrer_url: null, client_ip: null };
    }
  }

  // First external referrer of the session (e.g. facebook.com / instagram.com), kept across internal navigation
  _getReferrer() {
    try {
      const stored = sessionStorage.getItem('ms_ref');
      if (stored) return stored;
      const ref = document.referrer || '';
      if (ref && new URL(ref).host !== window.location.host) {
        sessionStorage.setItem('ms_ref', ref.slice(0, 512));
        return ref.slice(0, 512);
      }
    } catch (e) {}
    return null;
  }

  // Meta recommends IPv6 for client_ip_address. Our server usually only sees the
  // IPv4 hop, so the browser asks an IPv6-only endpoint for its public IPv6 (cached 1h).
  _detectIPv6() {
    try {
      const cached = JSON.parse(sessionStorage.getItem('ms_ip6') || 'null');
      if (cached && Date.now() - cached.t < 3600000) { this.clientIp = cached.ip || null; return; }
    } catch (e) {}
    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    if (ctrl) setTimeout(() => ctrl.abort(), 2500);
    fetch('https://api6.ipify.org?format=json', { signal: ctrl?.signal })
      .then((r) => r.json())
      .then((j) => {
        const ip = String(j?.ip || '');
        this.clientIp = ip.includes(':') ? ip : null;
        try { sessionStorage.setItem('ms_ip6', JSON.stringify({ ip: this.clientIp, t: Date.now() })); } catch (e) {}
        if (this.clientIp) this._identifyServer();
      })
      .catch(() => {
        try { sessionStorage.setItem('ms_ip6', JSON.stringify({ ip: null, t: Date.now() })); } catch (e) {}
      });
  }

  // Meta `contents` array: [{ id, quantity, item_price }]
  _contents(product, quantity = 1) {
    if (!product?.id) return [];
    return [{ id: String(product.id), quantity: Number(quantity) || 1, item_price: Number(product.price || 0) / 100 }];
  }

  // Store customer identifiers so every subsequent event is enriched for
  // browser Advanced Matching (fbq init 3rd param) and server-side CAPI hashing.
  setUserData(data = {}) {
    const pick = (k) => (typeof data[k] === 'string' ? data[k].trim() : '');
    if (pick('email')) this.userData.email = pick('email');
    if (pick('phone')) this.userData.phone = pick('phone');
    if (pick('name')) this.userData.name = pick('name');
    if (pick('city')) this.userData.city = pick('city');
    if (pick('state')) this.userData.state = pick('state');
    if (pick('zip')) this.userData.zip = pick('zip');
    if (pick('document')) this.userData.document = pick('document');
    try { localStorage.setItem('ms_pixel_ud', JSON.stringify(this.userData)); } catch (e) {}
    this.updateAdvancedMatching();
    this._identifyServer();
    try { window.dispatchEvent(new CustomEvent('ms:userdata')); } catch (e) {}
  }

  _loadUserData() {
    try {
      const raw = localStorage.getItem('ms_pixel_ud');
      if (raw) this.userData = { ...JSON.parse(raw), ...this.userData };
    } catch (e) {}
  }

  _fold(v) {
    return String(v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  }

  // Meta Advanced Matching payload (plain values; the pixel hashes them with SHA-256)
  getAdvancedMatching() {
    const u = this.userData || {};
    const am = {};
    if (u.email) am.em = u.email.toLowerCase().trim();
    if (u.phone) {
      let d = u.phone.replace(/\D/g, '');
      if (d.length === 10 || d.length === 11) d = '55' + d;
      if (d.length >= 12) am.ph = d;
    }
    if (u.name) {
      const parts = this._fold(u.name).split(/\s+/).filter(Boolean);
      if (parts.length) am.fn = parts[0];
      if (parts.length > 1) am.ln = parts[parts.length - 1];
    }
    if (u.city) am.ct = this._fold(u.city).replace(/[^a-z]/g, '');
    if (u.state) am.st = this._fold(u.state).replace(/[^a-z]/g, '').slice(0, 2);
    if (u.zip) { const z = u.zip.replace(/\D/g, ''); if (z.length >= 5) am.zp = z; }
    if (u.document) {
      // CPF as external_id — Meta accepts digits-only, the pixel hashes it
      const cpf = u.document.replace(/\D/g, '');
      if (cpf.length === 11) am.external_id = cpf;
    }
    am.country = 'br';
    return am;
  }

  // Re-init Facebook pixels with advanced matching data
  updateAdvancedMatching() {
    if (typeof window === 'undefined' || !window.fbq) return;
    const am = this.getAdvancedMatching();
    this.pixels.forEach(p => {
      if (p.is_active && p.platform === 'facebook') {
        try {
          // Disable Meta's "Automatic Setup Tool" per pixel. Without this, Meta
          // auto-detects buttons/links on the site (e.g. "Comprar") and fires
          // Purchase/Lead/etc WITHOUT value or currency — triggering the warning
          // "Envie códigos de moeda válidos" in Events Manager. We fire our own
          // clean events with value+currency ourselves.
          window.fbq('set', 'autoConfig', false, p.pixel_id);
          window.fbq('init', p.pixel_id, am);
        } catch (e) {}
      }
    });
  }

  // Persist the identifiers server-side keyed by _fbp so later events (even after
  // localStorage is cleared) can be enriched with real customer data.
  _identifyServer() {
    clearTimeout(this._identifyTimer);
    this._identifyTimer = setTimeout(() => {
      const fbp = this._ensureFbp();
      if (!fbp) return;
      const u = this.userData || {};
      fetch(`${API}/track/identify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fbp, email: u.email, phone: u.phone, name: u.name, city: u.city, state: u.state, zip: u.zip, document: u.document, client_ip: this.clientIp || undefined }),
        keepalive: true,
      }).catch(() => {});
    }, 800);
  }

  // Mirror an event to the backend Conversions API. Shares event_id with the
  // browser pixel so Meta/TikTok deduplicate the pair; adds IP, UA and cookies
  // that ad-blockers strip client-side -> higher match quality, more conversions.
  _serverTrack(eventName, { eventId, product, value, contentIds, contentName, contents, numItems, orderStatus } = {}) {
    try {
      // Guard: Purchase requires value > 0 — firing with 0 triggers Meta
      // Events Manager warnings ("valor ausente" / "código de moeda inválido").
      if (eventName === 'Purchase' && (!value || Number(value) <= 0)) {
        return;
      }
      const attr = this.getAttribution();
      let utmSource = null;
      try { utmSource = (getUtmParams() || {}).utm_source || null; } catch (e) {}
      const payload = {
        event_name: eventName,
        event_id: eventId,
        product_id: product?.id || null,
        content_ids: contentIds || (product?.id ? [product.id] : []),
        content_name: contentName || product?.name || null,
        value: value || 0,
        email: this.userData.email || null,
        phone: this.userData.phone || null,
        name: this.userData.name || null,
        city: this.userData.city || null,
        state: this.userData.state || null,
        zip: this.userData.zip || null,
        document: this.userData.document || null,
        fbp: attr.fbp,
        fbc: attr.fbc,
        ttp: this._getCookie('_ttp') || null,
        kwai_click_id: attr.kwai_click_id,
        event_source_url: attr.event_source_url,
        referrer_url: attr.referrer_url,
        client_ip: this.clientIp || undefined,
        contents: contents && contents.length ? contents : undefined,
        num_items: numItems || undefined,
        order_status: orderStatus || undefined,
        utm_source: utmSource || undefined,
      };
      fetch(`${API}/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    } catch (e) {}
  }

  // Load pixels from backend and initialize
  async init() {
    if (this.initialized) return;
    this._ensureFbp();
    this._loadUserData();
    this._detectIPv6();
    
    try {
      //console.log('PixelService: Loading pixels from API...');
      const response = await fetch(`${API}/settings/pixels`);
      const data = await response.json();
      this.pixels = data.pixels || [];
      this.trackingMode = data.tracking_mode || 'pixel_only';
      //console.log('PixelService: Loaded', this.pixels.length, 'pixels');

      // Admin turned Meta Pixel off in favour of the GTM Server-Side snippet.
      // Keep the service inert so no fbq/ttq events fire from the storefront.
      if (this.trackingMode === 'gtm_only') {
        this.pixels = [];
        this.initialized = true;
        this._flushPending();
        return;
      }
      
      // Initialize Facebook Pixel base code
      this.initFacebookBase();
      
      // Initialize TikTok Pixel base code
      this.initTikTokBase();
      if (this.pixels.some((p) => p.is_active && p.platform === 'kwai')) this.initKwaiBase();
      
      // Initialize each pixel
      this.pixels.forEach(pixel => {
        if (pixel.is_active) {
          //console.log('PixelService: Initializing', pixel.platform, 'pixel:', pixel.pixel_id);
          if (pixel.platform === 'facebook') {
            this.initFacebookPixel(pixel.pixel_id);
          } else if (pixel.platform === 'tiktok') {
            this.initTikTokPixel(pixel.pixel_id);
          } else if (pixel.platform === 'kwai') {
            this.initKwaiPixel(pixel.pixel_id);
          }
        }
      });
      
      this.initialized = true;
      //console.log('PixelService: Initialization complete');
      
      // Replay events fired before init (page-scoped PageView/ViewContent win over the generic one)
      this._flushPending();
      // Auto-track PageView after initialization
      setTimeout(() => {
        this.trackPageView();
      }, 100);
      
    } catch (error) {
      console.error('PixelService: Error loading pixels:', error);
      this.initialized = true;
      this._flushPending();
    }
  }

  // Initialize Facebook Pixel base code
  initFacebookBase() {
    if (this.facebookInitialized || typeof window === 'undefined') return;
    
    // Facebook Pixel base code
    !function(f,b,e,v,n,t,s) {
      if(f.fbq) return;
      n = f.fbq = function() {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if(!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    
    this.facebookInitialized = true;
  }

  // Initialize TikTok Pixel base code
  initTikTokBase() {
    if (this.tiktokInitialized || typeof window === 'undefined') return;
    
    // TikTok Pixel base code
    !function(w, d, t) {
      w.TiktokAnalyticsObject = t;
      var ttq = w[t] = w[t] || [];
      ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie", "holdConsent", "revokeConsent", "grantConsent"];
      ttq.setAndDefer = function(t, e) {
        t[e] = function() {
          t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
        };
      };
      for (var i = 0; i < ttq.methods.length; i++) {
        ttq.setAndDefer(ttq, ttq.methods[i]);
      }
      ttq.instance = function(t) {
        var e = ttq._i[t] || [];
        for (var n = 0; n < ttq.methods.length; n++) {
          ttq.setAndDefer(e, ttq.methods[n]);
        }
        return e;
      };
      ttq.load = function(e, n) {
        var i = "https://analytics.tiktok.com/i18n/pixel/events.js";
        ttq._i = ttq._i || {};
        ttq._i[e] = [];
        ttq._i[e]._u = i;
        ttq._t = ttq._t || {};
        ttq._t[e] = +new Date();
        ttq._o = ttq._o || {};
        ttq._o[e] = n || {};
        var o = document.createElement("script");
        o.type = "text/javascript";
        o.async = !0;
        o.src = i + "?sdkid=" + e + "&lib=" + t;
        var a = document.getElementsByTagName("script")[0];
        a.parentNode.insertBefore(o, a);
      };
    }(window, document, 'ttq');
    
    this.tiktokInitialized = true;
  }

  // Initialize a specific Facebook Pixel
  initFacebookPixel(pixelId) {
    if (typeof window !== 'undefined' && window.fbq) {
      // Disable Meta's "Automatic Setup Tool" BEFORE init — otherwise Meta
      // auto-detects buttons on the page and fires events (Purchase/Lead/etc)
      // WITHOUT value/currency, triggering the "moeda ausente" warning. We
      // handle all events explicitly with proper value+currency ourselves.
      try { window.fbq('set', 'autoConfig', false, pixelId); } catch (e) {}
      const am = this.getAdvancedMatching();
      if (Object.keys(am).length) window.fbq('init', pixelId, am);
      else window.fbq('init', pixelId);
    }
  }

  // Initialize a specific TikTok Pixel
  initTikTokPixel(pixelId) {
    if (typeof window !== 'undefined' && window.ttq) {
      window.ttq.load(pixelId);
      window.ttq.page();
      //console.log('TikTok Pixel initialized and page() called:', pixelId);
    } else {
      console.warn('TikTok ttq not available yet');
    }
  }

  // Kwai Pixel base code (official loader from Kwai for Business, lib name `kwaiq`)
  initKwaiBase() {
    if (this.kwaiInitialized || typeof window === 'undefined') return;
    !function(w, d, t) {
      w.KwaiAnalyticsObject = t;
      var q = w[t] = w[t] || [];
      q.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie"];
      var defer = function(obj, m) {
        obj[m] = function() {
          obj.push([m].concat(Array.prototype.slice.call(arguments, 0)));
        };
      };
      q.methods.forEach(function(m) { defer(q, m); });
      q.instance = function(id) {
        var inst = (q._i && q._i[id]) || [];
        q.methods.forEach(function(m) { defer(inst, m); });
        return inst;
      };
      q.load = function(id, opts) {
        var src = "https://s21-def.ap4r.com/kos/s101/nlav112572/pixel/events.js";
        q._i = q._i || {}; q._i[id] = []; q._i[id]._u = src;
        q._t = q._t || {}; q._t[id] = +new Date();
        q._o = q._o || {}; q._o[id] = opts || {};
        var qs = "?sdkid=" + id + "&lib=" + t;
        var s = d.createElement("script");
        s.type = "text/javascript"; s.async = true; s.src = src + qs;
        s.onerror = function() {
          var alt = d.createElement("script");
          alt.type = "text/javascript"; alt.async = true;
          alt.src = "https://s21-def.ks-la.net/kos/s101/nlav112572/pixel/events.js" + qs;
          var f0 = d.getElementsByTagName("script")[0];
          f0.parentNode.insertBefore(alt, f0);
        };
        var first = d.getElementsByTagName("script")[0];
        first.parentNode.insertBefore(s, first);
      };
    }(window, document, 'kwaiq');
    this.kwaiInitialized = true;
  }

  initKwaiPixel(pixelId) {
    if (typeof window === 'undefined' || !window.kwaiq) return;
    try {
      window.kwaiq.load(pixelId);
      window.kwaiq.page();
    } catch (e) {}
  }

  // Events fired before the pixel list is loaded are queued and replayed after init()
  // (ProductPage fires ViewContent ~1s after load, init runs at ~2s -> it used to be lost).
  _defer(fn) {
    if (this.initialized) return false;
    if (this._pending.length < 50) this._pending.push(fn);
    return true;
  }

  _flushPending() {
    const list = this._pending.splice(0);
    list.forEach((fn) => { try { fn(); } catch (e) {} });
  }

  // Get pixels that should track a specific product
  getPixelsForProduct(productId) {
    return this.pixels.filter(pixel => {
      if (!pixel.is_active) return false;
      // If no products specified, track all products
      if (!pixel.product_ids || pixel.product_ids.length === 0) return true;
      // Otherwise, check if this product is in the list
      return pixel.product_ids.map(String).includes(String(productId));
    });
  }

  // Check if a pixel should track a specific event
  shouldTrackEvent(pixel, eventName) {
    if (!pixel.events || pixel.events.length === 0) return true;
    return pixel.events.includes(eventName);
  }

  // Per-pixel product isolation: a pixel bound to specific products only receives its own
  // items; cart events are trimmed and re-valued. Returns null when the pixel must skip.
  _scope(pixel, contents, value, numItems) {
    const allowed = (pixel.product_ids || []).map(String);
    const list = contents || [];
    if (!allowed.length || !list.length) return { contents: list, contentIds: list.map((c) => String(c.id)), value, numItems };
    const kept = list.filter((c) => allowed.includes(String(c.id)));
    if (!kept.length) return null;
    if (kept.length === list.length) return { contents: list, contentIds: list.map((c) => String(c.id)), value, numItems };
    const qty = kept.reduce((s, c) => s + (Number(c.quantity) || 1), 0);
    const priced = kept.every((c) => Number.isFinite(Number(c.item_price)));
    const v = priced ? Math.round(kept.reduce((s, c) => s + Number(c.item_price) * (Number(c.quantity) || 1), 0) * 100) / 100 : value;
    return { contents: kept, contentIds: kept.map((c) => String(c.id)), value: v, numItems: qty };
  }

  // Fire ONLY on this pixel (fbq('track') would broadcast to every initialised pixel).
  _fireFb(pixel, eventName, params, eventId) {
    if (!window.fbq) return false;
    try { window.fbq('trackSingle', pixel.pixel_id, eventName, params, { eventID: eventId }); return true; } catch (e) { return false; }
  }

  _fireTt(pixel, eventName, params, eventId) {
    if (!window.ttq) return false;
    try {
      const inst = typeof window.ttq.instance === 'function' ? window.ttq.instance(pixel.pixel_id) : window.ttq;
      inst.track(eventName, params, { event_id: eventId });
      return true;
    } catch (e) { return false; }
  }

  _ttContents(contents) {
    return (contents || []).map((c) => ({ content_id: String(c.id), content_type: 'product', quantity: Number(c.quantity) || 1, price: Number(c.item_price) || 0 }));
  }

  // Kwai standard event names (kwaiq.track). PageView uses kwaiq.page() instead.
  static KWAI_EVENTS = {
    ViewContent: 'contentView', AddToCart: 'addToCart', InitiateCheckout: 'initiatedCheckout',
    AddPaymentInfo: 'addPaymentInfo', Purchase: 'purchase', Search: 'search', Lead: 'formSubmit',
  };

  // Fire on ONE Kwai pixel: `configs.pixelId` is the official way to target a pixel (Kwai GTM template)
  _fireKw(pixel, eventName, params) {
    if (!window.kwaiq) return false;
    const kwName = PixelService.KWAI_EVENTS[eventName];
    if (!kwName) return false;
    try { window.kwaiq.track(kwName, params, { pixelId: pixel.pixel_id }); return true; } catch (e) { return false; }
  }

  // Kwai params follow the Meta-like schema: contents[] + currency + value (+ content_name/query)
  _kwParams(scope, extra = {}) {
    const p = { currency: 'BRL', value: Number(scope.value) || 0, ...extra };
    if (scope.contents && scope.contents.length) {
      p.contents = this._ttContents(scope.contents);
      if (scope.contents.length === 1) {
        p.content_id = String(scope.contents[0].id);
        p.content_type = 'product';
        p.quantity = Number(scope.contents[0].quantity) || 1;
        p.price = Number(scope.contents[0].item_price) || 0;
      }
    }
    return p;
  }

  // Attribution filter: a pixel with `attribution_source` set only fires when the current
  // session's utm_source matches. Empty = fire for all traffic (default). Used to isolate
  // conversions when running the same product across multiple ad accounts.
  _matchesAttribution(pixel) {
    const required = String(pixel.attribution_source || "").trim().toLowerCase();
    if (!required) return true;
    try {
      const utm = getUtmParams();
      return String(utm.utm_source || "").trim().toLowerCase() === required;
    } catch (e) {
      return false;
    }
  }

  // Generic dispatcher: loops pixels, applies event + product scope, fires per pixel.
  // `build(scope)` returns { fb, tt, kw } param objects (kw optional -> derived). Returns true if any pixel fired.
  _dispatch(eventName, ttEventName, contents, value, numItems, eventId, build, pixels = this.pixels) {
    let fired = false;
    pixels.forEach((pixel) => {
      if (!pixel.is_active || !this.shouldTrackEvent(pixel, eventName)) return;
      if (!this._matchesAttribution(pixel)) return;
      const scope = this._scope(pixel, contents, value, numItems);
      if (!scope) return;
      const params = build(scope);
      if (pixel.platform === 'facebook') fired = this._fireFb(pixel, eventName, params.fb, eventId) || fired;
      else if (pixel.platform === 'tiktok' && ttEventName) fired = this._fireTt(pixel, ttEventName, params.tt, eventId) || fired;
      else if (pixel.platform === 'kwai') fired = this._fireKw(pixel, eventName, params.kw || this._kwParams(scope, { content_name: params.fb?.content_name })) || fired;
    });
    return fired;
  }

  // Track PageView event. Product pages -> only the pixels bound to that product;
  // pages without a product (home, search, cart) -> every active pixel.
  trackPageView(productId = null) {
    if (this._defer(() => this.trackPageView(productId))) return;
    if (!this.pixels.length) return;
    if (this._isDuplicate('PageView', window.location.pathname)) return;
    const pixels = productId ? this.getPixelsForProduct(productId) : this.pixels;
    const eventId = this._genEventId();
    let fired = false;

    pixels.forEach(pixel => {
      if (!pixel.is_active) return;
      if (!this.shouldTrackEvent(pixel, 'PageView')) return;
      if (!this._matchesAttribution(pixel)) return;
      if (pixel.platform === 'facebook') {
        fired = this._fireFb(pixel, 'PageView', {}, eventId) || fired;
      } else if (pixel.platform === 'tiktok' && window.ttq) {
        try { (typeof window.ttq.instance === 'function' ? window.ttq.instance(pixel.pixel_id) : window.ttq).page(); } catch (e) {}
      } else if (pixel.platform === 'kwai' && window.kwaiq) {
        // kwaiq.page() on init already covers the landing page; SPA route changes need it again
        if (this._kwaiPagedOnce) { try { window.kwaiq.page(); } catch (e) {} }
        this._kwaiPagedOnce = true;
      }
    });
    if (fired) this._serverTrack('PageView', { eventId, product: productId ? { id: productId } : null });
  }

  // Track ViewContent event
  trackViewContent(product) {
    if (this._defer(() => this.trackViewContent(product))) return;
    if (this._isDuplicate('ViewContent', product.id)) return;
    const eventId = this._genEventId();
    const contents = this._contents(product, 1);
    const value = product.price / 100;
    const fired = this._dispatch('ViewContent', 'ViewContent', contents, value, 1, eventId, (s) => ({
      fb: { content_type: 'product', content_ids: s.contentIds, contents: s.contents, content_name: product.name, value: s.value, currency: 'BRL' },
      tt: { content_id: String(product.id), content_type: 'product', content_name: product.name, value: s.value, currency: 'BRL' },
    }));
    if (fired) this._serverTrack('ViewContent', { eventId, product, value, contents, numItems: 1 });
  }

  // Track AddToCart event
  trackAddToCart(product, quantity = 1) {
    if (this._defer(() => this.trackAddToCart(product, quantity))) return;
    if (this._isDuplicate('AddToCart', product.id)) return;
    const eventId = this._genEventId();
    const contents = this._contents(product, quantity);
    const value = (product.price * quantity) / 100;
    const fired = this._dispatch('AddToCart', 'AddToCart', contents, value, quantity, eventId, (s) => ({
      fb: { content_type: 'product', content_ids: s.contentIds, contents: s.contents, content_name: product.name, value: s.value, currency: 'BRL', num_items: s.numItems },
      tt: { content_id: String(product.id), content_type: 'product', content_name: product.name, value: s.value, currency: 'BRL', quantity: s.numItems },
    }));
    if (fired) this._serverTrack('AddToCart', { eventId, product, value, contents, numItems: quantity });
  }

  // Track InitiateCheckout event. `opts.contentIds` overrides ids (cart), `opts.valueCents` overrides value.
  trackInitiateCheckout(product, quantity = 1, opts = {}) {
    if (this._defer(() => this.trackInitiateCheckout(product, quantity, opts))) return;
    const p = product || {};
    const eventId = this._genEventId();
    const contentIds = (opts.contentIds && opts.contentIds.length ? opts.contentIds : (p.id ? [p.id] : [])).map(String);
    const cents = Number.isFinite(opts.valueCents) ? opts.valueCents : (Number(p.price) || 0) * (Number(quantity) || 1);
    const value = Math.round(cents) / 100;
    const contents = opts.contents && opts.contents.length
      ? opts.contents
      : (p.id && p.id !== 'cart' ? this._contents(p, quantity) : contentIds.map((id) => ({ id, quantity: 1 })));
    const fired = this._dispatch('InitiateCheckout', 'InitiateCheckout', contents, value, quantity, eventId, (s) => ({
      fb: { content_type: 'product', content_ids: s.contentIds, contents: s.contents, content_name: p.name || 'checkout', value: s.value, currency: 'BRL', num_items: s.numItems },
      tt: { content_id: s.contentIds[0] || 'cart', content_type: 'product', content_name: p.name || 'checkout', value: s.value, currency: 'BRL', quantity: s.numItems, contents: this._ttContents(s.contents) },
    }));
    if (fired) this._serverTrack('InitiateCheckout', { eventId, product: p.id && p.id !== 'cart' ? p : null, contentIds, contentName: p.name || 'checkout', value, contents, numItems: quantity });
  }

  // Track Purchase — ONE event per order per pixel; pixels bound to products only get their items/value
  // `orderStatus`: 'pending' (PIX gerado) ou 'paid' (confirmado). Pixels com
  // purchase_rule='paid_only' são ignorados quando orderStatus==='pending'.
  trackPurchase(items, totalCents, orderId, orderStatus = 'paid') {
    if (this._defer(() => this.trackPurchase(items, totalCents, orderId, orderStatus))) return;
    const list = (Array.isArray(items) ? items : [items]).filter((i) => i && i.id);
    if (!list.length) return;
    // Guard: Purchase requires value > 0. Firing with 0 pollutes Event Match Quality.
    const valueCents = Math.round(Number(totalCents) || 0);
    if (valueCents <= 0) {
      try { console.warn('[pixelService] trackPurchase skipped: value <= 0', { orderId, totalCents }); } catch (e) {}
      return;
    }
    // Use orderId as the dedup key so the browser event and the server-side
    // (webhook-confirmed) Purchase collapse into a single conversion.
    const eventId = orderId ? String(orderId) : this._genEventId();
    // Dedup by orderId+status so one browser can fire pending AND paid for the same order
    const dedupKey = `${eventId}:${orderStatus}`;
    if (this._isDuplicate('Purchase', dedupKey)) return;
    const contentIds = list.map((i) => String(i.id));
    const contents = list.map((i) => ({ id: String(i.id), quantity: Number(i.quantity) || 1, item_price: Number(i.price || 0) / 100 }));
    const numItems = list.reduce((s, i) => s + (Number(i.quantity) || 1), 0);
    const value = valueCents / 100;
    const contentName = list.length === 1 ? list[0].name : `Pedido ${list.length} itens`;
    // Purchase rule ACTIVE: 'paid_and_pending' fires on pending + paid;
    // 'paid_only' skips the pending (PIX gerado) fire.
    const eligiblePixels = this.pixels.filter((p) => {
      if (orderStatus !== 'pending') return true;
      return (p.purchase_rule || 'paid_and_pending') === 'paid_and_pending';
    });
    const fired = this._dispatch('Purchase', 'CompletePayment', contents, value, numItems, eventId, (s) => ({
      fb: { content_type: 'product', content_ids: s.contentIds, contents: s.contents, content_name: contentName, value: s.value, currency: 'BRL', num_items: s.numItems, order_id: orderId },
      tt: { contents: this._ttContents(s.contents), value: s.value, currency: 'BRL', order_id: orderId },
    }), eligiblePixels);
    if (fired) this._serverTrack('Purchase', { eventId, product: list.length === 1 ? list[0] : null, contentIds, contents, numItems, contentName, value, orderStatus });
  }

  // Track AddPaymentInfo — customer clicked "pay" with a chosen method (pix | card).
  // `stableId` (optional, usually order_id from server response) makes the eventId
  // deterministic so this browser event deduplicates with the backend CAPI fire
  // (see helpers.py::fire_add_payment_info_capi). Without it, Meta Events Manager
  // flags low CAPI coverage ("servidor enviando N eventos a menos que o pixel").
  trackAddPaymentInfo(items = [], valueCents = 0, paymentMethod = 'pix', stableId = null) {
    if (this._defer(() => this.trackAddPaymentInfo(items, valueCents, paymentMethod, stableId))) return;
    const dedupKey = stableId ? `${stableId}:${paymentMethod}` : paymentMethod;
    if (this._isDuplicate('AddPaymentInfo', dedupKey)) return;
    const eventId = stableId ? `apinfo_${stableId}` : this._genEventId();
    const list = (items || []).filter((i) => i && i.id);
    const contentIds = list.map((i) => String(i.id));
    const contents = list.map((i) => ({ id: String(i.id), quantity: Number(i.quantity) || 1, item_price: Number(i.price || 0) / 100 }));
    const numItems = list.reduce((s, i) => s + (Number(i.quantity) || 1), 0) || 1;
    const value = Math.round(Number(valueCents) || 0) / 100;
    const fired = this._dispatch('AddPaymentInfo', 'AddPaymentInfo', contents, value, numItems, eventId, (s) => ({
      fb: { content_type: 'product', content_ids: s.contentIds, contents: s.contents, value: s.value, currency: 'BRL', num_items: s.numItems, payment_method: paymentMethod },
      tt: { content_id: s.contentIds[0] || 'cart', content_type: 'product', value: s.value, currency: 'BRL', contents: this._ttContents(s.contents) },
    }));
    if (fired) this._serverTrack('AddPaymentInfo', { eventId, contentIds, contents, numItems, value, contentName: paymentMethod });
  }

  // Track Search — storefront search submitted with results (no product scope: every pixel)
  trackSearch(query, results = []) {
    if (this._defer(() => this.trackSearch(query, results))) return;
    const q = String(query || '').trim();
    if (!q || this._isDuplicate('Search', q.toLowerCase())) return;
    const eventId = this._genEventId();
    const contentIds = (results || []).slice(0, 10).map((p) => String(p.id));
    const fired = this._dispatch('Search', 'Search', [], 0, 0, eventId, () => ({
      fb: { search_string: q, content_type: 'product', content_ids: contentIds },
      tt: { query: q },
      kw: { query: q, currency: 'BRL', value: 0 },
    }));
    if (fired) this._serverTrack('Search', { eventId, contentIds, contentName: q });
  }

  // Track Lead (no product scope: every pixel)
  trackLead(data = {}) {
    if (this._defer(() => this.trackLead(data))) return;
    const eventId = this._genEventId();
    const fired = this._dispatch('Lead', 'SubmitForm', [], 0, 0, eventId, () => ({ fb: data, tt: data, kw: { ...data, currency: 'BRL', value: 0 } }));
    if (fired) this._serverTrack('Lead', { eventId, contentName: data.content_name });
  }
}

// Export singleton instance
const pixelService = new PixelService();
export default pixelService;
